import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { partitionBy, sortBy, groupBy } from 'lib/array/arrayUtils'
import { shallowEqual } from 'hooks/reduxHooks'
import { SetRequired } from 'type-fest'
import { queryNullChildren, queryChildrenDelimiter } from 'constants/search'
import qs from 'qs'
import { isString } from 'lib/string/stringUtils'
import { generateOfferPageRoom, initialOfferPageRooms, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getOccupancyFromSearchStrings } from 'lib/search/searchUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { isTodayBeforeCheckin } from 'components/Search/utils'
import { isSameDay } from 'lib/datetime/dateUtils'
import { stringParam } from 'lib/url/queryStringUtils'
import { queryKeyAdults, queryKeyBundledOfferId, queryKeyCheckIn, queryKeyCheckOut, queryKeyChildren, queryKeyDaysUntilCheckIn, queryKeyDuration, queryKeyMultiRoomMode, queryKeyRateId, queryKeyRoomId } from 'constants/url'

export interface PackageOptionsByRoom {
  roomId: string,
  packageViews: Array<App.OfferPackageOptionView>
}

interface SortOrderDefinedOfferPackageOptionView extends App.OfferPackageOptionView {
  package: SetRequired<App.HotelPackage, 'sortOrder'>
}

const isSortOrderDefined = (opt: App.OfferPackageOptionView): opt is SortOrderDefinedOfferPackageOptionView => !!opt.package.sortOrder
const isAvailable = (opt: App.OfferPackageOptionView) => opt.hotelAvailable && opt.capacityValid && !!opt.hasAvailableRates

const sortOrderDefinedOptions = (options: Array<SortOrderDefinedOfferPackageOptionView>) => {
  return options.toSorted((a, b) => {
    // Sort by sortOrder first
    if (a.package.sortOrder < b.package.sortOrder) return -1
    if (a.package.sortOrder > b.package.sortOrder) return 1
    // Sort by price if sortOrder is the same
    return a.hotelPrice - b.hotelPrice
  })
}

/**
 * Sort list of package options based on 'sortOrder' logic
 */
export function sortPackageOptions(packageOptions: Array<App.OfferPackageOptionView>) {
  const hasTactical = packageOptions.some(p => p.package.hasTactical)
  const [availablePackageOptions, unavailablePackageOptions] = partitionBy(packageOptions, isAvailable)
  const [definedOrderPackageOptions, undefinedOrderPackageOptions] = partitionBy<App.OfferPackageOptionView, SortOrderDefinedOfferPackageOptionView>(availablePackageOptions, isSortOrderDefined)
  const [definedOrderUnavailablePackageOptions, undefinedOrderUnavailablePackageOptions] = partitionBy<App.OfferPackageOptionView, SortOrderDefinedOfferPackageOptionView>(unavailablePackageOptions, isSortOrderDefined)

  let primaryPackageOptions = sortOrderDefinedOptions(definedOrderPackageOptions)
  let secondaryPackageOptions = sortBy(undefinedOrderPackageOptions, opt => opt.hotelPrice, 'asc')
  let tertiaryPackageOptions = sortBy(undefinedOrderUnavailablePackageOptions, opt => opt.hotelPrice, 'asc')
  let quaternaryPackageOptions = sortOrderDefinedOptions(definedOrderUnavailablePackageOptions)

  if (isLPCTacticalEnabled() && hasTactical) {
    secondaryPackageOptions = sortBy(secondaryPackageOptions, opt => opt.package.hasTactical ? 1 : 2, 'asc')
    tertiaryPackageOptions = sortBy(tertiaryPackageOptions, opt => opt.package.hasTactical ? 1 : 2, 'asc')
    quaternaryPackageOptions = sortBy(quaternaryPackageOptions, opt => opt.package.hasTactical ? 1 : 2, 'asc')
    primaryPackageOptions = sortBy(primaryPackageOptions, opt => opt.package.hasTactical ? 1 : 2, 'asc')
  }

  return [...primaryPackageOptions, ...secondaryPackageOptions, ...tertiaryPackageOptions, ...quaternaryPackageOptions]
}

/**
 * The logic for organising the order of rooms and the package options for that room.
 *
 * @remarks
 * - 'sortOrder' is an optional package option field
 * - By sorting the packages first, we take advantage of 'Map' insertion order
 *   This means we can guarantee the order of rooms is also sorted
 *
 * @param packageOptions - List of package options to be organised
 * @returns organised list of PackageOptionByRoom
 */
export function buildRoomPackageOptionsViewOrder(packageOptions: Array<App.OfferPackageOptionView>) {
  const sortedPackageOptions = sortPackageOptions(packageOptions)
  const sortedPackageOptionsByRoomMap = groupBy(sortedPackageOptions, option => option.package.roomType?.id ?? '')
  return Array.from(
    sortedPackageOptionsByRoomMap.entries()).reduce<Array<PackageOptionsByRoom>>((acc, [key, val]) => {
    return [...acc, { roomId: key, packageViews: val }]
  }, [])
}

// TEMP: Remove this logic when "006RF00000IRdvlYAD" becomes unavailable
function getSkiPackageType(pkg: App.HotelPackage): 'Saver' | 'Value' | 'Peak' | undefined {
  if (pkg.name.includes('Saver')) return 'Saver'
  if (pkg.name.includes('Value')) return 'Value'
  if (pkg.name.includes('Peak')) return 'Peak'
}

export function filterPackageOptionsBySkiPackageType(packageOptions: Array<App.OfferPackageOptionView>, cheapestSelectedOption?: App.OfferPackageOptionView) {
  const isKosciuszkoChalet = packageOptions.length ? packageOptions[0].package.offerId === '006RF00000IRdvlYAD' : false
  if (!isKosciuszkoChalet || !cheapestSelectedOption) return { skiPackageFiltered: false, packageOptions }
  const currentType = getSkiPackageType(cheapestSelectedOption.package)
  const filteredOptions = packageOptions.filter(pkg => currentType === getSkiPackageType(pkg.package))
  return { skiPackageFiltered: true, packageOptions: filteredOptions }
}

/**
 * Using the same logic as buildRoomPackageOptionsViewOrder just with additional sorting step:
 * Options in LPC should go in the next way:
 * - the cheapest options
 * - options with bonus inclusions
 * - options without bonus inclusions
 *
 * @param packageOptions - List of package options to be organised
 * @param LPCAdvancedSortingEnabled
 * @returns organised list of PackageOptionByRoom
 */
export function buildRoomPackageOptionsViewOrderLPC(packageOptions: Array<App.OfferPackageOptionView>) {
  const sortedPackageOptions = sortPackageOptions(packageOptions)
  const sortedPackageOptionsByRoomMap = groupBy(sortedPackageOptions, option => option.package.roomType?.id ?? '')
  return Array.from(
    sortedPackageOptionsByRoomMap.entries()).reduce<Array<PackageOptionsByRoom>>((acc, [key, val]) => {
    let options = val
    if (options.length > 1) {
      const optionsWithBonusInclusions: Array<App.OfferPackageOptionView> = []
      const optionsWithoutBonusInclusions: Array<App.OfferPackageOptionView> = []

      for (const option of options) {
        if (option.package?.bonusInclusions.length || option.package?.luxPlusInclusionsByTier !== undefined) {
          optionsWithBonusInclusions.push(option)
        } else {
          optionsWithoutBonusInclusions.push(option)
        }
      }
      options = [...optionsWithBonusInclusions, ...optionsWithoutBonusInclusions]
    }

    return [...acc, { roomId: key, packageViews: options }]
  }, [])
}

export function preventRenderWithOpenFilter(props: any, nextProps: any) {
  // Allow for a render as it changes from not open -> open
  // so both current and next props have to both be open
  if (nextProps.open && props.open) {
    return true
  }
  return shallowEqual(props, nextProps)
}

export function preventRenderWithClosedFilter(props: any, nextProps: any) {
  // Allow for a render as it changes from open -> not open
  // so both current and next props have to both be closed
  if (!nextProps.open && !props.open) {
    return true
  }
  return shallowEqual(props, nextProps)
}

function splitByComma(queryParam: any) {
  return isString(queryParam) ? queryParam.split(',') : []
}

function buildOfferPageRooms(
  adults = '',
  children = queryNullChildren,
  maxChildAge?: number,
  maxInfantAge?: number,
  roomId?: string,
  rateId?: string,
): Array<OfferPageRoom> {
  const allAdults = splitByComma(adults).map(adult => Number(adult))
  const allChildren = splitByComma(children)
  const roomIds = splitByComma(roomId).map(id => id || undefined)
  const rateIds = splitByComma(rateId).map(id => id || undefined)
  const roomCount = Math.max(allAdults.length, allChildren.length)

  const occupants: Array<OfferPageRoom> = []

  for (let i = 0; i < roomCount; i++) {
    const childrenAgeList = allChildren[i]

    let children: Array<number> = []
    if (childrenAgeList && childrenAgeList !== queryNullChildren) {
      children = childrenAgeList.split(queryChildrenDelimiter)
        .map(age => parseInt(age))
        .filter(age => !isNaN(age))
    }

    const occupancy = getOccupancyFromSearchStrings(allAdults[i], children, maxChildAge, maxInfantAge)
    occupants.push(generateOfferPageRoom({ ...occupancy, roomId: roomIds[i] || undefined, rateId: rateIds[i] || undefined }))
  }

  return occupants
}

export function parseOfferPageRooms(
  search: string,
  maxChildAge?: number,
  maxInfantAge?: number,
): Array<OfferPageRoom> {
  const params = qs.parse(search, { ignoreQueryPrefix: true, duplicates: 'combine' })

  const adults = stringParam(params[queryKeyAdults])
  const children = stringParam(params[queryKeyChildren])
  const roomId = stringParam(params[queryKeyRoomId])
  const rateId = stringParam(params[queryKeyRateId])

  if (Array.isArray(adults) || Array.isArray(children)) {
    // incorrectly formatted query string, we shouldn't get arrays
    return []
  }

  if (!adults && (!children || children === queryNullChildren)) {
    return []
  }

  return buildOfferPageRooms(adults, children, maxChildAge, maxInfantAge, roomId, rateId)
}

// extract page state data from query params
export function decodeOfferPageParams(searchStr: string, maxChildAge?: number, maxInfantAge?: number): {
  rooms: Array<OfferPageRoom>
  dates: {
    checkIn?: string
    checkOut?: string
  }
  bundledOfferId?: string
  duration?: number
  multiRoomMode?: OfferPageMultiRoomMode
} {
  const params = qs.parse(searchStr, { ignoreQueryPrefix: true, duplicates: 'first' }) as Record<string, string | undefined>
  const {
    [queryKeyCheckIn]: checkIn,
    [queryKeyCheckOut]: checkOut,
    [queryKeyDaysUntilCheckIn]: daysUntilCheckIn,
    [queryKeyDuration]: duration,
  } = params

  const parsedRooms = parseOfferPageRooms(searchStr, maxChildAge, maxInfantAge)

  const relativeCheckIn = daysUntilCheckIn && moment().add(parseInt(daysUntilCheckIn), 'days').format(ISO_DATE_FORMAT)
  const relativeCheckOut = daysUntilCheckIn && duration && moment().add(parseInt(daysUntilCheckIn) + parseInt(duration), 'days').format(ISO_DATE_FORMAT)

  const parsedDates = {
    checkIn: checkIn || relativeCheckIn || undefined,
    checkOut: checkOut || relativeCheckOut || undefined,
  }
  if (parsedDates.checkIn && !(isTodayBeforeCheckin(parsedDates.checkIn) || isSameDay(new Date(parsedDates.checkIn), new Date()))) {
    parsedDates.checkIn = undefined
    parsedDates.checkOut = undefined
  }

  const parsedDuration = Number(duration) || undefined

  return {
    rooms: parsedRooms,
    dates: (parsedDates.checkIn && parsedDates.checkOut) ? parsedDates : { checkIn: undefined, checkOut: undefined },
    bundledOfferId: params[queryKeyBundledOfferId],
    duration: !(parsedDates.checkIn && parsedDates.checkOut) ? parsedDuration : undefined,
    multiRoomMode: parsedRooms.length > 1 ? params[queryKeyMultiRoomMode] as OfferPageMultiRoomMode : undefined,
  }
}

export function hasSelectedRooms(rooms: Array<OfferPageRoom> | undefined) {
  return !!rooms && rooms.length > 0 && rooms !== initialOfferPageRooms && !rooms.some(room => room.childrenAge?.some(age => age === -1))
}
